import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Paper from '@mui/material/Paper';
import { useState } from 'react';
import { User } from 'lucide-react';
import debounce from 'lodash.debounce';
import { ChipList, DataGrid, FilterSearch, FilterDateRange, Spinner } from '@repo/components';
import { ProjectForm } from '@repo/forms';
import { EventEmitter, guardedPost, guardedPut, stringToSort, constructQuery } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getCompanyProjects } from 'src/api/company';
import { useLocalStorage, useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/company_/$groupId/projects/')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		search: (search.search as string) || '',
		page: (search.page as number) || 0,
		sort: (search.sort as string) || 'name:asc',
		from: (search.from as string) || '',
		until: (search.until as string) || '',
		tab: (search.tab as string) || '0',
	}),
});

const columns = [
	{ field: 'name', headerName: 'Name' },
	{
		field: 'startDate',
		headerName: 'Start Date',
		valueFormatter: (params: any) => params.value && new Date(params.value).toLocaleDateString('en-GB'),
	},
	{
		field: 'endDate',
		headerName: 'End Date',
		valueFormatter: (params: any) => params.value && new Date(params.value).toLocaleDateString('en-GB'),
	},
	{
		field: 'principalGroupsPopulated',
		headerName: 'Responsible Companies',
		renderCell: (c: any) => {
			if (!c.value || !Array.isArray(c.value)) return null;
			const uniqueGroups = c.value.filter(
				(group: any, index: number, self: any[]) => index === self.findIndex((g) => g.id === group.id),
			);
			return <ChipList data={uniqueGroups} getLabel={(group: any) => group.name} />;
		},
	},
	{
		field: 'zones',
		headerName: 'Zones',
		flex: 1,
		renderCell: (c: any) => <ChipList data={c.value} getLabel={(o: any) => o?.name} />,
		sortable: false,
	},
	{
		field: 'admins',
		headerName: 'Contacts',
		flex: 1,
		renderCell: (c: any) => (
			<ChipList
				data={c.value?.map((user: any) => ({ ...user, status: undefined }))}
				getLabel={(o: any) => (o.lastName ? `${o?.firstName} ${o?.lastName}` : o?.email)}
				icon={<User size={14} className="text-gray-500" />}
			/>
		),
		sortable: false,
	},
	{
		field: 'updatedAt',
		headerName: 'Updated',
		valueFormatter: (params: any) => params.value && new Date(params.value).toLocaleDateString('en-GB'),
	},
];

function RouteComponent() {
	const { groupId } = Route.useParams();
	const searchParams = Route.useSearch();
	const navigate = useNavigate();
	const updateQueryParams = useUpdateQueryParams();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();
	const { updateValue } = useLocalStorage<string>('companyPortalProjectsBackUrl');

	const [isFormOpen, setIsFormOpen] = useState(false);
	const [activeProject, setActiveProject] = useState<any>(undefined);

	const query = constructQuery({
		page: searchParams.page,
		sort: searchParams.sort,
		search: searchParams.search || undefined,
		from: searchParams.from || undefined,
		until: searchParams.until || undefined,
	});

	const { data: projectsApiResponse, isLoading } = useQuery({
		queryKey: ['company-projects', groupId, searchParams],
		queryFn: () => getCompanyProjects(groupId, query),
		enabled: !userLoading,
	});

	useBreadcrumbLevel(1, 'Projects');

	if (userLoading) {
		return null;
	}

	if (!authenticatedUser) return null;

	const revalidateProjects = () => {
		queryClient.invalidateQueries({ queryKey: ['company-projects', groupId] });
	};

	const handleCloseForm = () => {
		setIsFormOpen(false);
		setActiveProject(undefined);
	};

	const handleSubmit = async (data: any) => {
		let isError;
		let id;
		if (data) {
			const method = data.id ? guardedPut : guardedPost;
			await method(`/api/projects/${data.id || ''}`, data)
				.then(({ body }: any) => {
					EventEmitter.emit('toast', { type: 'success', message: 'Project saved' });
					id = body.id;
				})
				.catch((error: any) => {
					isError = true;
					EventEmitter.emit('toast', { type: 'error', message: error?.message || 'Failed to save Project.' });
				});
		}
		if (id) {
			updateValue(window.location.href);
			navigate({ to: '/company/$groupId/projects/$projectId/events', params: { groupId, projectId: id } });
		} else {
			revalidateProjects();
		}
		if (!isError) {
			setIsFormOpen(false);
			setActiveProject(undefined);
		}
	};

	const handleRowClick = ({ row }: any) => {
		if (!row) return;
		updateValue(window.location.href);
		navigate({ to: '/company/$groupId/projects/$projectId/events', params: { groupId, projectId: row.id } });
	};

	const handleSort = ([sort]: any[]) => {
		if (!sort) return;
		updateQueryParams({ sort: `${sort.field}:${sort.sort}` });
	};

	const handleDateFilterChange = (dateFilter: { from: string; until: string }) => {
		updateQueryParams(dateFilter);
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const handleSearchChange = (searchValue: string) => {
		updateQueryParams({ search: searchValue });
	};

	const activeTab = parseInt(searchParams.tab || '0', 10);

	return (
		<div>
			<div className="p-4 pt-0">
				<div className="mt-2 flex flex-row gap-2">
					<div className="w-3/4">
						<FilterSearch
							label="Search Project"
							defaultValue={searchParams.search || ''}
							onChange={debounce((searchInput: string) => handleSearchChange(searchInput), 350)}
							sx={{ width: '100%' }}
						/>
					</div>
					{activeTab === 0 && (
						<div className="w-1/4">
							<FilterDateRange
								label="Filter Date Range"
								value={
									searchParams.from || searchParams.until
										? { from: searchParams.from, until: searchParams.until }
										: undefined
								}
								onChange={handleDateFilterChange}
								sx={{ width: '100%' }}
							/>
						</div>
					)}
				</div>
				<div className="mb-4" />
				<Paper variant="outlined">
					<DataGrid
						data={projectsApiResponse?.data || []}
						rowCount={projectsApiResponse?.count || 0}
						page={projectsApiResponse?.page || 0}
						pageSize={projectsApiResponse?.pageSize || 0}
						columns={columns}
						isLoading={isLoading}
						sortModel={[stringToSort(searchParams.sort)]}
						onSortModelChange={handleSort}
						onReload={revalidateProjects}
						onPaginationModelChange={handlePaginationModelChange}
						paginationMode="server"
						sortingMode="server"
						sortingOrder={['asc', 'desc']}
						onRowClick={handleRowClick}
					/>
				</Paper>
			</div>
			<ProjectForm
				isOpen={isFormOpen || !!activeProject}
				onClose={handleCloseForm}
				onSubmit={handleSubmit}
				data={activeProject || {}}
			/>
		</div>
	);
}
